// Gantt-like timeline: one row per item, grouped by status. Bars span start → due; items with only
// a due date are milestones (diamonds). Drag a bar to move it (keeps duration), drag its edges to
// resize; arrow keys move by one day (Shift+arrows resize the end).
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Item, Status, UpdateItem } from "../api/types";
import { Icon } from "../components/icons";
import { CATEGORY_LABELS, formatYmdWith, isClosed, itemYmd, shiftIsoDays, startHasTime } from "../lib/format";
import { addDaysYmd, diffDaysYmd, todayYmd, weekdayMon0 } from "../lib/tz";
import { patchItem, toastError } from "../store/app";
import { useApp } from "../store/hooks";

type Zoom = "week" | "month";
const ZOOM: Record<Zoom, { dayW: number; before: number; span: number; step: number }> = {
  week: { dayW: 40, before: 7, span: 49, step: 7 },
  month: { dayW: 14, before: 21, span: 140, step: 28 },
};
const ROW_H = 34;

type DragMode = "move" | "start" | "end";
interface Drag {
  id: string;
  mode: DragMode;
  x0: number;
  delta: number;
  moved: boolean;
}

interface Bar {
  item: Item;
  start: string | null; // ymd
  due: string | null; // ymd
}

function barOf(item: Item): Bar | null {
  const due = item.due_at ? itemYmd(item.due_at, item.due_has_time) : null;
  const start = item.start_at ? itemYmd(item.start_at, startHasTime(item.start_at)) : null;
  if (!due && !start) return null;
  return { item, start, due };
}

/** API patch for a drag result. */
export function timelinePatch(item: Item, mode: DragMode, delta: number): UpdateItem | null {
  if (!delta) return null;
  const sHas = startHasTime(item.start_at);
  const patch: UpdateItem = {};
  if (mode === "move") {
    if (item.start_at) patch.start_at = shiftIsoDays(item.start_at, sHas, delta);
    if (item.due_at) patch.due_at = shiftIsoDays(item.due_at, item.due_has_time, delta);
  } else if (mode === "start") {
    if (!item.start_at) return null;
    let d = delta;
    if (item.due_at) {
      const span = diffDaysYmd(itemYmd(item.start_at, sHas), itemYmd(item.due_at, item.due_has_time));
      d = Math.min(delta, span);
    }
    if (!d) return null;
    patch.start_at = shiftIsoDays(item.start_at, sHas, d);
  } else {
    if (!item.due_at) return null;
    let d = delta;
    if (item.start_at) {
      const span = diffDaysYmd(itemYmd(item.start_at, sHas), itemYmd(item.due_at, item.due_has_time));
      d = Math.max(delta, -span);
    }
    if (!d) return null;
    patch.due_at = shiftIsoDays(item.due_at, item.due_has_time, d);
  }
  return patch;
}

function apply(item: Item, mode: DragMode, delta: number) {
  const patch = timelinePatch(item, mode, delta);
  if (patch) patchItem(item.id, patch).catch((e) => toastError(e, "Could not reschedule"));
}

export function TimelineView({ items, statuses }: { items: Item[]; statuses: Status[] }) {
  useApp((s) => s.timezone);
  const navigate = useNavigate();
  const location = useLocation();
  const today = todayYmd();
  const [zoom, setZoom] = useState<Zoom>("week");
  const cfg = ZOOM[zoom];
  const [origin, setOrigin] = useState(() => addDaysYmd(today, -ZOOM.week.before));
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const scrollRef = useRef<HTMLDivElement>(null);

  const days = useMemo(() => Array.from({ length: cfg.span }, (_, i) => addDaysYmd(origin, i)), [origin, cfg.span]);
  const end = days[days.length - 1];

  const bars = useMemo(() => items.filter((i) => i.type !== "meeting").map(barOf).filter((b): b is Bar => !!b), [items]);
  const undated = items.filter((i) => i.type !== "meeting" && !i.start_at && !i.due_at).length;

  const groups = useMemo(() => {
    const out: { key: string; label: string; category: Status["category"]; color: string | null; bars: Bar[] }[] = [];
    const byStatus = new Map<string, Bar[]>();
    for (const b of bars) {
      const list = byStatus.get(b.item.status_id) ?? [];
      list.push(b);
      byStatus.set(b.item.status_id, list);
    }
    const sortBars = (list: Bar[]) =>
      list.sort((a, b) => (a.start ?? a.due ?? "").localeCompare(b.start ?? b.due ?? "") || a.item.title.localeCompare(b.item.title));
    for (const s of statuses) {
      const list = byStatus.get(s.id);
      if (list?.length) out.push({ key: s.id, label: s.name, category: s.category, color: s.color, bars: sortBars(list) });
      byStatus.delete(s.id);
    }
    for (const [key, list] of byStatus) {
      const cat = list[0].item.status_category;
      out.push({ key, label: CATEGORY_LABELS[cat], category: cat, color: null, bars: sortBars(list) });
    }
    return out;
  }, [bars, statuses]);

  // Pointer dragging (window listeners while a drag is active).
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = Math.round((e.clientX - d.x0) / cfg.dayW);
      if (delta !== d.delta || (!d.moved && Math.abs(e.clientX - d.x0) > 3)) {
        setDrag({ ...d, delta, moved: d.moved || Math.abs(e.clientX - d.x0) > 3 });
      }
    };
    const onUp = () => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const item = items.find((i) => i.id === d.id);
      if (!item) return;
      if (!d.moved) {
        navigate(`/items/${item.id}`, { state: { background: location } });
        return;
      }
      apply(item, d.mode, d.delta);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("keydown", onKey);
    };
    // Only (re)bind when a drag starts/ends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag !== null, cfg.dayW]);

  const startDrag = (e: ReactPointerEvent, id: string, mode: DragMode) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id, mode, x0: e.clientX, delta: 0, moved: false });
  };

  const setZoomLevel = (z: Zoom) => {
    setZoom(z);
    setOrigin(addDaysYmd(today, -ZOOM[z].before));
  };

  const x = (ymd: string) => diffDaysYmd(origin, ymd) * cfg.dayW;
  const todayX = x(today);

  // Header rows: months + days (week zoom) or week starts (month zoom).
  const months: { label: string; from: number; width: number }[] = [];
  for (const d of days) {
    const key = d.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.label === key) last.width += cfg.dayW;
    else months.push({ label: key, from: x(d), width: cfg.dayW });
  }
  const width = days.length * cfg.dayW;

  return (
    <div className="tl">
      <div className="cal-toolbar">
        <div className="cal-nav">
          <button type="button" className="icon-btn" aria-label="Earlier" onClick={() => setOrigin((o) => addDaysYmd(o, -cfg.step))}>
            <Icon name="chevronLeft" />
          </button>
          <button type="button" className="btn small" onClick={() => setOrigin(addDaysYmd(today, -cfg.before))}>
            Today
          </button>
          <button type="button" className="icon-btn" aria-label="Later" onClick={() => setOrigin((o) => addDaysYmd(o, cfg.step))}>
            <Icon name="chevronRight" />
          </button>
        </div>
        <h2 className="cal-month">
          {formatYmdWith(origin, { month: "short", day: "numeric" })} – {formatYmdWith(end, { month: "short", day: "numeric", year: "numeric" })}
        </h2>
        <div className="seg" role="group" aria-label="Zoom">
          {(["week", "month"] as Zoom[]).map((z) => (
            <button key={z} type="button" className={zoom === z ? "active" : ""} aria-pressed={zoom === z} onClick={() => setZoomLevel(z)}>
              {z === "week" ? "Weeks" : "Months"}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="muted pad">No items with start or due dates. Set dates in an item to see it here.</p>
      ) : (
        <div className="tl-scroll" ref={scrollRef}>
          <div className="tl-inner" style={{ width: width + 220 }}>
            <div className="tl-head" style={{ gridTemplateColumns: `220px ${width}px` }}>
              <div className="tl-corner">Item</div>
              <div className="tl-scale" style={{ width }}>
                <div className="tl-months">
                  {months.map((m) => (
                    <div key={m.label} className="tl-month" style={{ left: m.from, width: m.width }}>
                      {formatYmdWith(`${m.label}-01`, { month: zoom === "week" ? "long" : "short", year: "numeric" })}
                    </div>
                  ))}
                </div>
                <div className="tl-days">
                  {days.map((d) => {
                    const wd = weekdayMon0(d);
                    if (zoom === "month" && wd !== 0) return null;
                    return (
                      <div
                        key={d}
                        className={`tl-day${wd >= 5 ? " weekend" : ""}${d === today ? " today" : ""}`}
                        style={{ left: x(d), width: zoom === "week" ? cfg.dayW : cfg.dayW * 7 }}
                      >
                        {zoom === "week" ? (
                          <>
                            <span className="tl-wd">{formatYmdWith(d, { weekday: "narrow" })}</span>
                            {Number(d.slice(8))}
                          </>
                        ) : (
                          Number(d.slice(8))
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="tl-body" style={{ backgroundSize: `${cfg.dayW * (zoom === "week" ? 1 : 7)}px 100%` }}>
              {todayX >= 0 && todayX <= width && (
                <div className="tl-today" style={{ left: 220 + todayX + cfg.dayW / 2 }} aria-hidden="true" />
              )}
              {groups.map((g) => (
                <section key={g.key} className="tl-group" aria-label={g.label}>
                  <div className="tl-group-head">
                    <span className={`cat-dot cat-${g.category}`} style={g.color ? { background: g.color } : undefined} />
                    {g.label}
                    <span className="count">{g.bars.length}</span>
                  </div>
                  {g.bars.map((b) => {
                    const d = drag && drag.id === b.item.id ? drag : null;
                    let s = b.start ?? b.due!;
                    let e = b.due ?? b.start!;
                    if (d) {
                      if (d.mode === "move") {
                        s = addDaysYmd(s, d.delta);
                        e = addDaysYmd(e, d.delta);
                      } else if (d.mode === "start") {
                        s = addDaysYmd(s, Math.min(d.delta, diffDaysYmd(s, e)));
                      } else {
                        e = addDaysYmd(e, Math.max(d.delta, -diffDaysYmd(s, e)));
                      }
                    }
                    const milestone = !b.start || !b.due;
                    const left = x(s);
                    const w = (diffDaysYmd(s, e) + 1) * cfg.dayW;
                    const done = isClosed(b.item.status_category);
                    const label = `${b.item.title}: ${b.start ? `start ${b.start}` : ""}${b.start && b.due ? ", " : ""}${b.due ? `due ${b.due}` : ""}`;
                    return (
                      <div key={b.item.id} className="tl-row" style={{ height: ROW_H }}>
                        <button
                          type="button"
                          className={`tl-label${done ? " done" : ""}`}
                          onClick={() => navigate(`/items/${b.item.id}`, { state: { background: location } })}
                          title={b.item.title}
                        >
                          {b.item.title || "(untitled)"}
                        </button>
                        <div className="tl-track" style={{ width }}>
                          {milestone ? (
                            <button
                              type="button"
                              className={`tl-milestone cat-${b.item.status_category}${d ? " dragging" : ""}${b.due ? "" : " start-only"}`}
                              style={{ left: left + cfg.dayW / 2 - 8 }}
                              aria-label={`${label} (milestone). Arrow keys move by a day.`}
                              title={label}
                              onPointerDown={(ev) => startDrag(ev, b.item.id, "move")}
                              onKeyDown={(ev) => keyMove(ev, b.item)}
                              onClick={(ev) => ev.preventDefault()}
                            />
                          ) : (
                            <div
                              className={`tl-bar cat-${b.item.status_category}${done ? " done" : ""}${d ? " dragging" : ""}`}
                              style={{ left, width: w }}
                              title={label}
                            >
                              <span
                                className="tl-handle start"
                                aria-hidden="true"
                                onPointerDown={(ev) => startDrag(ev, b.item.id, "start")}
                              />
                              <button
                                type="button"
                                className="tl-bar-body"
                                aria-label={`${label}. Arrow keys move, Shift+arrows change the end date.`}
                                onPointerDown={(ev) => startDrag(ev, b.item.id, "move")}
                                onKeyDown={(ev) => keyMove(ev, b.item)}
                                onClick={(ev) => ev.preventDefault()}
                              >
                                <span className="tl-bar-text">{b.item.title}</span>
                              </button>
                              <span
                                className="tl-handle end"
                                aria-hidden="true"
                                onPointerDown={(ev) => startDrag(ev, b.item.id, "end")}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
      {undated > 0 && (
        <p className="muted small tl-foot">
          {undated} item{undated === 1 ? "" : "s"} without dates not shown.
        </p>
      )}
    </div>
  );

  function keyMove(ev: ReactKeyboardEvent, item: Item) {
    if (ev.key === "Enter") {
      navigate(`/items/${item.id}`, { state: { background: location } });
      return;
    }
    if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
    ev.preventDefault();
    const delta = ev.key === "ArrowLeft" ? -1 : 1;
    apply(item, ev.shiftKey ? "end" : "move", delta);
  }
}
